// src/components/share/ShareToFriendModalRedux.tsx
"use client";

import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "../common";
import { AppDispatch } from "../../redux/storeTypes";
import {
  selectFriendsList,
  selectFriendshipLoading,
} from "../../redux/selectors/network/friendshipSelectors";
import { fetchFriendsList } from "../../redux/network/friendshipSlice";
import { createNewDialogue, sendMessage } from "../../redux/conversation/conversationSlice";
import { AvatarDisplay, SearchInput } from "../common";
import "./ShareToFriendModalRedux.css";

type Preview = {
  poster?: string;
  ownerHandle?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  url: string;
  title?: string;
  preview?: Preview;
};

export default function ShareToFriendModalRedux({ open, onClose, url, title, preview }: Props) {
  const dispatch: AppDispatch = useDispatch();

  const friends = useSelector(selectFriendsList) || [];
  const loading = useSelector(selectFriendshipLoading) || false;

  const [q, setQ] = React.useState("");
  const [sendingId, setSendingId] = React.useState<number | null>(null);

  // fetch friends on first open if empty
  const fetchedOnceRef = React.useRef(false);
  React.useEffect(() => {
    if (!open) return;
    if (friends.length > 0) return;
    if (loading) return;
    if (fetchedOnceRef.current) return;
    fetchedOnceRef.current = true;
    dispatch(fetchFriendsList());
  }, [open, friends.length, loading, dispatch]);

  // search filter
  const filtered = React.useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return friends;
    return friends.filter((f: any) => {
      const name = (f.name || "").toLowerCase();
      const username = (f.username || "").toLowerCase();
      return name.includes(term) || username.includes(term);
    });
  }, [q, friends]);

  // ensure dialogue then send a RICH payload (as JSON-string)
  const ensureDialogueAndSend = async (toUserId: number) => {
    const check = await dispatch(
      createNewDialogue({ recipient_id: toUserId, check_only: true })
    ).unwrap();

    let slug: string | null = check?.dialogue?.slug ?? null;
    if (!slug) {
      const created = await dispatch(
        createNewDialogue({ recipient_id: toUserId })
      ).unwrap();
      slug = created?.dialogue?.slug ?? null;
    }
    if (!slug) throw new Error("Dialogue slug not found");

    // build a structured content for rich rendering
    const payload = {
      kind: "shared_video",
      url,
      title: title || null,
      poster: preview?.poster || null,
      ownerHandle: preview?.ownerHandle || null,
      ts: Date.now(),
    };

    await dispatch(
      sendMessage({
        dialogue_slug: slug,
        content: JSON.stringify(payload),
      })
    ).unwrap();
  };

  const handleSend = async (friendId: number) => {
    if (sendingId) return;
    try {
      setSendingId(friendId);
      await ensureDialogueAndSend(friendId);
      onClose();
      // TODO: toast success
    } catch (e) {
      // TODO: toast error
    } finally {
      setSendingId(null);
    }
  };

  const Row = ({ f }: { f: any }) => {
    const isSending = sendingId === f.id;
    const borderColor = f.label.color;

    return (
      <button
        type="button"
        onClick={() => !sendingId && handleSend(f.id)}
        disabled={!!sendingId}
        className="share-row"
      >
        <AvatarDisplay
          userId={f.id}
          src={f.avatar_cdn_url || null}
          placeholderSrc={f.avatar_url || f.profile_image || null}
          alt={f.username}
          size="md"
          shape="rounded"
          withBorder={!!borderColor}
          borderColor={borderColor}
          isVerifiedIdentity={!!f?.is_verified}
          isTownlitVerified={!!f?.is_townlit_verified}
          title={f.username || f.name}
        />
        <div className="share-row__main">
          <div className="share-row__name" title={f.name || f.username}>
            {f.name || f.username}
          </div>
          <div className="share-row__handle" title={`@${f.username}`}>
            @{f.username}
          </div>
        </div>
        <div className="share-row__status">
          {isSending ? <span className="share-row__sending">Sending…</span> : null}
        </div>

      </button>
    );
  };

  return (
    <Modal isOpen={open} onClose={onClose} title="Share with a friend">
      <div className="share-modal">
        {/* search */}
        <SearchInput
          value={q}
          onChange={setQ}
          placeholder="Search friends…"
          ariaLabel="Search friends"
          size="md"
          className="share-modal__search"
          loading={loading}
        />

        {/* tiny preview */}
        <div className="share-modal__preview">
          <span className="share-modal__preview-label">Link:</span>{" "}
          <a href={url} target="_blank" rel="noreferrer" className="share-modal__preview-link">
            {url}
          </a>
          {title ? (
            <>
              {" "}
              — <span className="share-modal__preview-title">{title}</span>
            </>
          ) : null}
        </div>

        {/* list */}
        <div className="share-modal__list">
          {loading ? (
            <div className="share-modal__empty">Loading friends…</div>
          ) : filtered.length === 0 ? (
            <div className="share-modal__empty">No friends found.</div>
          ) : (
            <div className="share-modal__items">
              {filtered.map((f: any) => (
                <Row key={f.id} f={f} />
              ))}
            </div>
          )}
        </div>

      </div>
    </Modal>
  );
}
